'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/navigation';

import { VipVoucherGrant } from '@flipster/apis/v1/voucher/types';
import { FormattedMessage, T } from '@flipster/i18n';
import { Button } from '@flipster/react/design-system/button';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { css } from '@flipster/styled-system/css';
import { hstack, square, stack } from '@flipster/styled-system/patterns';
import Num from '@flipster/utils/num';

import { useIsWebview } from '../../../../shared/webview';
import { voucherActivateBunja } from '../state';
import vipBadge from './vip-badge.webp';

const getFeeDiscountPercent = (defaultFee: string, trialFee: string): string => {
  const defaultNum = Num(defaultFee);
  if (defaultNum.eq(0)) return '0';
  return String(Math.round(Num(1).minus(Num(trialFee).div(defaultNum)).times(100).toNumber()));
};

interface VipTrialBannerProps {
  voucher: VipVoucherGrant;
}

export const VipTrialBanner = ({ voucher }: VipTrialBannerProps) => {
  const webview = useIsWebview();
  const { tier, defaultMakerFee, defaultTakerFee, trialMakerFee, trialTakerFee } = voucher;
  const router = useRouter();
  const { activate, isActivatingAtom } = useBunja(voucherActivateBunja);
  const isActivating = useAtomValue(isActivatingAtom);

  const makerDiscountPercent = getFeeDiscountPercent(defaultMakerFee, trialMakerFee);
  const takerDiscountPercent = getFeeDiscountPercent(defaultTakerFee, trialTakerFee);

  const handleActivate = async () => {
    try {
      await activate(voucher.grantId, 'VIP');
      snackbar.success({
        message: <T id='VIP.r260213.Vip_tier_trial_started_fee_benefits_are_now_active' values={{ tier }} />,
      });
      router.push(webview ? '/fee-vouchers' : '/reward-hub/v2/vouchers');
      // eslint-disable-next-line no-empty
    } catch {}
  };

  return (
    <div
      className={hstack({
        w: '100%',
        justify: 'space-between',
        rounded: 12,
        bg: 'background.default.level-3',
        gap: 8,
        tabletDown: {
          px: 16,
          py: 12,
        },
        tablet: { p: 16 },
      })}
    >
      <div className={hstack({ gap: { base: 8, tablet: 12 } })}>
        <img src={vipBadge.src} alt='VIP badge' className={square({ size: 40 })} />
        <div className={stack({ gap: 4 })}>
          <p className={css({ textStyle: 'Text/M/Strong', color: 'content.default.level-5' })}>
            <FormattedMessage id='VIP.r260213.Start_your_vip_tier_trial' values={{ tier }} />
          </p>
          <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>
            <FormattedMessage id='VIP.r260213.Maker_fee_percent_off' values={{ percent: `${makerDiscountPercent}%` }} />
            {' · '}
            <FormattedMessage id='VIP.r260213.Taker_fee_percent_off' values={{ percent: `${takerDiscountPercent}%` }} />
          </p>
        </div>
      </div>
      <Button
        label={<FormattedMessage id='Autogen_common.r251126.s_Start' />}
        onClick={handleActivate}
        isLoading={isActivating}
        css={{ flexShrink: 0 }}
      />
    </div>
  );
};
